import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import moment from '@g4/moment-utc';

import { DEPARTING, RETURNING, tripTypeValues } from '../constants';
import {
  ERROR_DEPARTING_AFTER_RETURNING,
  ERROR_RETURNING_BEFORE_DEPARTING,
  WARNING_DEPARTING_AFTER_RETURNING,
} from '../../../../automationHooks.json';

// The invalid state is when the departing flight date / time is
// after the returning flight date / time.
const types = {
  WARNING: 'warning',
  ERROR: 'error',
};
const invalidStates = {
  // Displayed when a departing tab change will land
  // in an invalid state. Return flight is moved
  // + 3 days from depart date, and warning is shown on return tab
  WARNING_DEPARTING_AFTER_RETURNING: {
    type: types.WARNING,
    key: 'warningDepartingAfterReturning',
    displayOn: RETURNING,
    disable: null,
    hook: WARNING_DEPARTING_AFTER_RETURNING.hook,
  },
  // Displayed when a selected depart flight lands
  // at a later time than the return flight time.
  ERROR_DEPARTING_AFTER_RETURNING: {
    type: types.ERROR,
    key: 'errorDepartingAfterReturning',
    displayOn: DEPARTING,
    disable: RETURNING,
    hook: ERROR_DEPARTING_AFTER_RETURNING.hook,
  },
  // Displayed when a selected return flight leaves
  // at a later time than the depart flight landing time.
  ERROR_RETURNING_BEFORE_DEPARTING: {
    type: types.ERROR,
    key: 'errorReturningBeforeDeparting',
    displayOn: RETURNING,
    disable: RETURNING,
    hook: ERROR_RETURNING_BEFORE_DEPARTING.hook,
  },
};

export const isDateAfterDate = (departingDate, returningDate) => {
  return (
    departingDate &&
    returningDate &&
    moment(departingDate).isAfter(moment(returningDate))
  );
};

export const useWarning = (
  tripType,
  tabDates,
  updateTabDates,
  lastAvailableDates,
  setDepartingTabs,
  setReturningTabs,
  stadiumGame,
  departingQuery
) => {
  // const [selectedRaidersGame, setSelectedRaidersGame] = useState(undefined);
  const [lastAvailableFlightsDate, setLastAvailableFlightsDate] = useState(
    undefined
  );

  useEffect(() => {
    if (!stadiumGame) {
      return;
    }
    const departingFlights = departingQuery?.flights;
    const sortedFlightDates = departingFlights.sort((a, b) =>
      moment(a.flight.departingTime).diff(moment(b.flight.departingTime))
    );

    let availableFlightDate;
    sortedFlightDates.forEach(flight => {
      if (
        moment(
          moment(flight.flight.departingTime).format('YYYY-MM-DD')
        ).isBefore(moment(stadiumGame?.date))
      ) {
        availableFlightDate = moment(flight.flight.departingTime).format(
          'YYYY-MM-DD'
        );
      }
    });
    setLastAvailableFlightsDate(availableFlightDate);
  }, [departingQuery, stadiumGame, setLastAvailableFlightsDate]);

  const [warning, setWarning] = useState(null);

  const gameDay = useMemo(
    () => stadiumGame?.date && moment(stadiumGame.date).format('YYYY-MM-DD'),
    [stadiumGame]
  );

  useEffect(() => {
    if (
      gameDay &&
      moment(tabDates[DEPARTING].middle).isAfter(moment(gameDay))
    ) {
      if (
        lastAvailableFlightsDate &&
        tabDates[DEPARTING].middle !==
          moment(lastAvailableFlightsDate).format('YYYY-MM-DD')
      ) {
        setDepartingTabs({ middle: lastAvailableFlightsDate });
      }
    }
  }, [gameDay, lastAvailableFlightsDate, setDepartingTabs, tabDates]);

  const deactivateWarning = useCallback(() => setWarning(null), [setWarning]);

  const [
    shouldReturnFlightOptionBeNull,
    setShouldReturnFlightOptionBeNull,
  ] = useState(false);

  const validateAndAdjustReturnDateOnNewDepartDate = useCallback(
    newDepartingTabDate => {
      if (
        !isDateAfterDate(newDepartingTabDate, tabDates[RETURNING]?.selected)
      ) {
        deactivateWarning();
        return null;
      }
      setWarning(invalidStates.WARNING_DEPARTING_AFTER_RETURNING);
      setShouldReturnFlightOptionBeNull(true);
      return moment
        .min(
          moment(newDepartingTabDate).add(3, 'days'),
          moment(lastAvailableDates[RETURNING])
        )
        .format('YYYY-MM-DD');
    },
    [
      tabDates,
      deactivateWarning,
      setShouldReturnFlightOptionBeNull,
      setWarning,
      lastAvailableDates,
    ]
  );

  // Wrapps the departing tab setter to validate
  // and to switch the returning tab automatically
  const setDepartingTabsAndValidate = useCallback(
    newDepartingTabs => {
      if (isSameAsPrevious(tabDates[DEPARTING], newDepartingTabs)) {
        return;
      }
      let newDepartDate = newDepartingTabs.selected;
      if (
        !!gameDay &&
        moment(newDepartingTabs.selected).isSameOrAfter(moment(gameDay))
      ) {
        // This logic is intended to prevent auto selecting any flight after game day
        // when the user is navigating between dates
        newDepartDate = lastAvailableFlightsDate;
      }

      const newReturnDate =
        tripType !== tripTypeValues.ONEWAY &&
        validateAndAdjustReturnDateOnNewDepartDate(newDepartDate);

      if (newReturnDate) {
        updateTabDates({
          [DEPARTING]: {
            selected: newDepartDate,
            ...newDepartingTabs,
          },
          [RETURNING]: {
            selected: newReturnDate,
            middle: newReturnDate,
          },
        });
      } else {
        setDepartingTabs({
          selected: newDepartDate,
          ...newDepartingTabs,
        });
      }
    },
    [
      setDepartingTabs,
      lastAvailableFlightsDate,
      gameDay,
      updateTabDates,
      tabDates,
      tripType,
      validateAndAdjustReturnDateOnNewDepartDate,
    ]
  );

  // Wrapps the returning tab setter to deactivate
  const setReturningTabsAndClearWarning = useCallback(
    (...args) => {
      isSameAsPrevious(tabDates[RETURNING], args);
      deactivateWarning();
      setReturningTabs(...args);
    },
    [deactivateWarning, setReturningTabs, tabDates]
  );

  return {
    warning,
    deactivateWarning,
    setDepartingTabsAndValidate,
    setReturningTabsAndClearWarning,
    shouldReturnFlightOptionBeNull,
    setShouldReturnFlightOptionBeNull,
  };
};

const isSameAsPrevious = (prevTabs, newTabs) => {
  return (
    newTabs.selected &&
    newTabs.selected === prevTabs.selected &&
    newTabs.middle &&
    newTabs.middle === prevTabs.middle
  );
};

export const useError = (tripType, selectedFlightOptions, updatedLeg) => {
  const latestUpdatedLeg = updatedLeg.current;
  return useMemo(() => {
    if (
      tripType === tripTypeValues.ROUNDTRIP &&
      isDateAfterDate(
        get(selectedFlightOptions[DEPARTING], 'flight.arrivalTime'),
        get(selectedFlightOptions[RETURNING], 'flight.departingTime')
      )
    ) {
      return latestUpdatedLeg === DEPARTING
        ? invalidStates.ERROR_DEPARTING_AFTER_RETURNING
        : invalidStates.ERROR_RETURNING_BEFORE_DEPARTING;
    }

    return null;
  }, [tripType, selectedFlightOptions, latestUpdatedLeg]);
};
